import React, { useRef } from 'react';
import { Eye, KeyRound, Pencil, Send, Trash2, UserCheck, UserX } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../../ui/sheet';
import { Avatar } from '../kit';
import { displayName } from './userModel';

// Phone row actions. Takes the same items the desktop row menu gets
// (UsersPage rowActions) and lays them out as two groups plus Cancel.
const GROUPS = [
  ['edit', 'resend', 'password', 'emulate'],
  ['deactivate', 'restore', 'delete'],
];
const ICONS = {
  edit: Pencil,
  resend: Send,
  password: KeyRound,
  emulate: Eye,
  deactivate: UserX,
  restore: UserCheck,
  delete: Trash2,
};
// Destructive rows use the error ink; Restore access is a recovery action.
const DANGER = new Set(['deactivate', 'delete']);

/**
 * Bottom sheet for one user's actions (phones).
 *   user: the row (null ⇒ closed) · items: rowActions(user) · onClose()
 */
export default function UserActionSheet({ user, items = [], onClose }) {
  const ran = useRef(false);
  const visible = items.filter(Boolean);
  const groups = GROUPS
    .map((keys) => keys.map((k) => visible.find((it) => it.key === k)).filter(Boolean))
    .filter((g) => g.length);
  const name = displayName(user);

  const run = (item) => {
    ran.current = true;
    onClose();
    item.onSelect();
  };

  return (
    <Sheet open={Boolean(user)} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent
        side="bottom"
        hideClose
        className="ak-m-sheet"
        // An action opens its own dialog or panel; don't pull focus back to the row.
        onCloseAutoFocus={(e) => { if (ran.current) { e.preventDefault(); ran.current = false; } }}
      >
        <span aria-hidden="true" className="ak-m-grabber" />
        {user ? (
          <>
            <div className="ak-m-sheet-head">
              <Avatar name={name} email={user.email} />
              <div className="ak-m-sheet-title">
                <SheetTitle className="text-base">{name}</SheetTitle>
                <SheetDescription className="text-[color:var(--lodge-onyx-500)]">{user.email}</SheetDescription>
              </div>
            </div>
            {groups.map((group, i) => (
              <div key={group[0].key} className="ak-m-sheet-group" role="group" aria-label={i === 0 ? 'Actions' : 'Access'}>
                {group.map((it) => {
                  const Icon = ICONS[it.key];
                  return (
                    <button
                      key={it.key}
                      type="button"
                      className={`ak-m-sheet-row${DANGER.has(it.key) || it.danger ? ' ak-m-danger' : ''}`}
                      disabled={it.disabled}
                      onClick={() => run(it)}
                    >
                      {Icon ? <Icon aria-hidden="true" /> : null}
                      <span>{it.label}</span>
                    </button>
                  );
                })}
              </div>
            ))}
            <div className="ak-m-sheet-group">
              <button type="button" className="ak-m-sheet-row ak-m-cancel" onClick={onClose}>Cancel</button>
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
